/**
 * Storage operations for location redirect popup
 * @module location-redirect-popup/storage
 */

import { STORAGE_KEY, CHOICE_SAVED_EVENT } from './constants.js';

export interface StoredData {
  country?: string;
  pendingFrom?: string;
}

export interface SaveChoiceOptions {
  silent?: boolean;
}

/**
 * @typedef {Object} StoredData
 * @property {string} [country] - The confirmed country code the user chose
 * @property {string} [pendingFrom] - The country code user came from (awaiting confirmation)
 */

/**
 * Gets the full stored data from localStorage
 * @returns {StoredData|null} The stored data or null if not found
 */
export function getStoredData(): StoredData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    return JSON.parse(stored) as unknown as StoredData;
  } catch (e) {
    console.error('[Location Redirect] Error reading stored data:', e);
    return null;
  }
}

/**
 * Gets the stored country code from localStorage
 * @returns {string|null} The stored country code or null if not found
 */
export function getStoredCountry(): string | null {
  const data = getStoredData();
  return data?.country || null;
}

/**
 * Saves user's confirmed location choice to localStorage
 * @param {string} countryCode - The country code
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - If true, skip dispatching the choice-saved event (e.g. before a redirect)
 */
export function saveChoice(countryCode: string, options?: SaveChoiceOptions): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        country: countryCode,
      }),
    );
    if (!options?.silent) {
      document.dispatchEvent(new CustomEvent(CHOICE_SAVED_EVENT));
    }
    console.info('[Location Redirect] Choice saved:', { countryCode });
  } catch (e) {
    console.error('[Location Redirect] Failed to save choice:', e);
  }
}

/**
 * Clears the stored location choice from localStorage
 * Used before showing a popup so downstream features know the choice is unresolved
 */
export function clearChoice(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/**
 * Saves pending state when user is switching locales via footer
 * @param {string} fromCountryCode - The country code user is leaving from
 */
export function savePendingSwitch(fromCountryCode: string): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        pendingFrom: fromCountryCode,
      }),
    );
    console.info('[Location Redirect] Pending switch saved:', { fromCountryCode });
  } catch (e) {
    console.error('[Location Redirect] Failed to save pending switch:', e);
  }
}
